import { ja } from './ja'

export type Dict = typeof ja

/** URL のプレフィックス、html の lang、hreflang に同じ値（BCP 47）を使う */
export const LOCALES = [
  'ja',
  'en',
  'zh-CN',
  'zh-TW',
  'ko',
  'es',
  'pt-BR',
  'fr',
  'de',
  'id',
  'vi',
  'ru',
  'it',
  'tr',
  'pl',
  'th',
  'hi',
] as const

export type Locale = (typeof LOCALES)[number]

// 日本語以外の辞書は、サーバーでは全部を最初から持ち、ブラウザでは表示する言語の分だけ読み込む（最初に送る JS を軽くする）
type DictModule = Record<string, Dict>
const LAZY = import.meta.glob<DictModule>(['./*.ts', '!./index.ts', '!./ja.ts'])
const EAGER: Record<string, DictModule> = import.meta.env.SSR
  ? import.meta.glob<DictModule>(['./*.ts', '!./index.ts', '!./ja.ts'], { eager: true })
  : {}

const DICTS: Partial<Record<Locale, Dict>> = { ja }
for (const [file, mod] of Object.entries(EAGER)) DICTS[localeOfFile(file)] = Object.values(mod)[0]

function localeOfFile(file: string): Locale {
  return file.slice(2, -3) as Locale
}

/** 各言語は、その言語自身の表記で名乗る */
export const LOCALE_NAMES: Record<Locale, string> = {
  ja: '日本語',
  en: 'English',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  ko: '한국어',
  es: 'Español',
  'pt-BR': 'Português (Brasil)',
  fr: 'Français',
  de: 'Deutsch',
  id: 'Bahasa Indonesia',
  vi: 'Tiếng Việt',
  ru: 'Русский',
  it: 'Italiano',
  tr: 'Türkçe',
  pl: 'Polski',
  th: 'ไทย',
  hi: 'हिन्दी',
}

const OG_LOCALES: Record<Locale, string> = {
  ja: 'ja_JP',
  en: 'en_US',
  'zh-CN': 'zh_CN',
  'zh-TW': 'zh_TW',
  ko: 'ko_KR',
  es: 'es_ES',
  'pt-BR': 'pt_BR',
  fr: 'fr_FR',
  de: 'de_DE',
  id: 'id_ID',
  vi: 'vi_VN',
  ru: 'ru_RU',
  it: 'it_IT',
  tr: 'tr_TR',
  pl: 'pl_PL',
  th: 'th_TH',
  hi: 'hi_IN',
}

export function ogLocale(locale: Locale): string {
  return OG_LOCALES[locale]
}

/** Intl に渡す言語タグ。en だけは日付の並びを米国式に固定する */
export function intlTag(locale: Locale): string {
  return locale === 'en' ? 'en-US' : locale
}

/** 漢字圏の言語。ハンコや章番号を和文の意匠で出す */
export function isCjk(locale: Locale): boolean {
  return locale === 'ja' || locale === 'zh-CN' || locale === 'zh-TW' || locale === 'ko'
}

export function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v)
}

/** ルートの `{-$locale}` を解釈する。省略は ja、ja 以外の既知の言語でなければ null */
export function parseLocale(param: string | undefined): Locale | null {
  if (param === undefined || param === '') return 'ja'
  return param !== 'ja' && isLocale(param) ? param : null
}

/** その言語の辞書を使えるようにする。ブラウザで描く前（起動時と言語をまたぐ遷移の前）に呼ぶ */
export async function loadDict(locale: Locale): Promise<void> {
  if (DICTS[locale]) return
  const mod = await LAZY[`./${locale}.ts`]()
  DICTS[locale] = Object.values(mod)[0]
}

export function getDict(locale: Locale): Dict {
  const dict = DICTS[locale]
  if (dict) return dict
  console.error(`[i18n] ${locale} の辞書がまだ読み込まれていません`)
  return ja
}

export function localePath(locale: Locale, path: string): string {
  if (locale === 'ja') return path
  return path === '/' ? `/${locale}` : `/${locale}${path}`
}

/** パス先頭の言語プレフィックスを読む。なければ ja */
export function localeFromPathname(pathname: string): Locale {
  const seg = pathname.split('/')[1] ?? ''
  return parseLocale(seg) ?? 'ja'
}

export function stripLocale(pathname: string): string {
  const locale = localeFromPathname(pathname)
  if (locale === 'ja') return pathname
  const rest = pathname.slice(locale.length + 1)
  return rest === '' ? '/' : rest
}

export function fmt(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m))
}
